// ExerciseMedia — small leaf that renders an exercise's demo media with
// defence in depth against broken paths.
//
// Why this is its own component: the previous version kept `mediaBroken`
// state in the parent (LiveWorkoutRunner / StepDetailModal) and tried to
// reset it via `key={exercise.id}` on the wrapper. That failed because
// (a) once `mediaBroken` is true the render guard `mediaUrl && !mediaBroken`
// unmounts the keyed subtree so its key never changes, and (b) even on an
// exercise swap the parent state survives, so a fine-media exercise
// rendered next is hidden for the rest of that mount. Putting the state
// in a child keyed by exercise.id means a real React unmount/remount on
// every exercise change, and the new instance starts with mediaBroken=false.

import { useState } from "react";
import type { JSX } from "react";
import type { Exercise } from "../../types/calisthenics";
import type { LocalizedText } from "../../types/calisthenics";

export interface ExerciseMediaProps {
  exercise: Pick<Exercise, "id" | "mediaUrl" | "name">;
  locale: keyof LocalizedText;
  /** Tailwind/classes for the inner <img>. Caller picks the size. */
  imgClassName?: string;
}

export function ExerciseMedia({
  exercise,
  locale,
  imgClassName = "max-h-48 w-auto rounded-[var(--radius-sharp)] object-contain",
}: ExerciseMediaProps): JSX.Element | null {
  const [mediaBroken, setMediaBroken] = useState(false);
  if (!exercise.mediaUrl || mediaBroken) return null;
  return (
    <div className="flex justify-center overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-line)] bg-black/40 p-2">
      <img
        src={exercise.mediaUrl}
        alt={exercise.name[locale]}
        className={imgClassName}
        loading="lazy"
        onError={() => setMediaBroken(true)}
      />
    </div>
  );
}